import { supabase } from "../lib/supabase";

export interface PdfFileRow {
  id: string;
  user_id: string;
  folder_id: string | null;
  file_name: string;
  file_path: string;
  file_size: number;
  page_count: number;
  current_page: number;
  auto_fullscreen: boolean;
  thumbnail_url: string | null;
  share_token: string | null;
  share_enabled: boolean;
  share_password: string | null;
  share_expiry: string | null;
  share_created_at: string | null;
  event_url: string | null;
  event_visible: boolean | null;
  event_active: boolean | null;
  event_timestamp: number | null;
  event_number: number | null;
  created_at: string;
  updated_at: string;
  status?: string;
  original_path?: string;
  [key: string]: unknown;
}

export async function findPdfsByUser(userId: string): Promise<PdfFileRow[]> {
  const { data, error } = await supabase
    .from("pdf_files")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as PdfFileRow[];
}

export async function findPdfById(pdfId: string): Promise<PdfFileRow> {
  const { data, error } = await supabase
    .from("pdf_files")
    .select("*")
    .eq("id", pdfId)
    .single();

  if (error || !data) throw error ?? new Error("PDF를 찾을 수 없음");
  return data as PdfFileRow;
}

export async function findPdfFields<T = Record<string, unknown>>(
  pdfId: string,
  fields: string,
): Promise<T | null> {
  const { data, error } = await supabase
    .from("pdf_files")
    .select(fields)
    .eq("id", pdfId)
    .maybeSingle();

  if (error) throw error;
  return data as T | null;
}

export async function getSignedUrl(filePath: string): Promise<string> {
  const { data: urlData, error: urlError } = await supabase.storage
    .from("pdfs")
    .createSignedUrl(filePath, 3600);

  if (!urlError && urlData?.signedUrl) return urlData.signedUrl;

  // fallback: public URL
  const { data: publicUrlData } = supabase.storage
    .from("pdfs")
    .getPublicUrl(filePath);

  return publicUrlData.publicUrl;
}

export async function deletePdf(
  pdfId: string,
  filePath: string,
  thumbnailUrl: string | null,
): Promise<void> {
  // 스토리지에서 PDF 삭제
  await supabase.storage.from("pdfs").remove([filePath]);

  // 썸네일 삭제
  if (thumbnailUrl) {
    const thumbPath = thumbnailUrl.split("/thumbnails/")[1];
    if (thumbPath) {
      await supabase.storage.from("thumbnails").remove([thumbPath]);
    }
  }

  // DB 레코드 삭제
  const { error } = await supabase
    .from("pdf_files")
    .delete()
    .eq("id", pdfId);

  if (error) throw error;
}

export async function renamePdf(
  pdfId: string,
  newFileName: string,
): Promise<void> {
  const { error } = await supabase
    .from("pdf_files")
    .update({ file_name: newFileName })
    .eq("id", pdfId);

  if (error) throw error;
}

export async function updateAutoFullscreen(
  pdfId: string,
  userId: string,
  enabled: boolean,
): Promise<void> {
  if (enabled) {
    // 다른 PDF 모두 비활성화
    await supabase
      .from("pdf_files")
      .update({ auto_fullscreen: false })
      .eq("user_id", userId)
      .eq("auto_fullscreen", true);

    // 선택한 PDF 활성화
    const { error } = await supabase
      .from("pdf_files")
      .update({ auto_fullscreen: true })
      .eq("id", pdfId);

    if (error) throw error;
  } else {
    const { error } = await supabase
      .from("pdf_files")
      .update({ auto_fullscreen: false })
      .eq("id", pdfId);

    if (error) throw error;
  }
}

export async function updatePdfFolder(
  pdfId: string,
  folderId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from("pdf_files")
    .update({ folder_id: folderId })
    .eq("id", pdfId);

  if (error) throw error;
}

export async function setShareSettings(params: {
  pdfId: string;
  shareEnabled: boolean;
  shareToken: string | null;
  sharePassword: string;
  shareExpiry: string;
  shareCreatedAt: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("set_share_settings", {
    p_pdf_id: params.pdfId,
    p_share_enabled: params.shareEnabled,
    p_share_token: params.shareToken,
    p_share_password: params.sharePassword,
    p_share_expiry: params.shareExpiry,
    p_share_created_at: params.shareCreatedAt,
  });

  if (error) throw error;
}
